import { Modal } from "@amzn/storm-ui";
import { isEmpty, isUndefined } from "lodash";
import * as React from "react";
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { TELEMETRY_ID } from "../../constants/telemetry";
import { useSfAccountsAction } from "../../hooks/salesforce/useSfAccountsAction";
import { isError } from "../../utils/api.utils";
import { logWithTakt } from "../../utils/telemetry.utils";

import AccountDropdown from "./AccountDropdown";
import { AccountPickerError } from "./AccountPickerError";
import { AccountPickerFooter } from "./AccountPickerFooter";

export interface AccountPickerProps {
    onAccountSelect: (accountId: string) => void;
    onCancel: () => void;
    onClose: () => void;
    shouldNavigateOnCancel?: boolean;
}

export const AccountPicker: React.FunctionComponent<AccountPickerProps> = ({
    onAccountSelect,
    onCancel,
    onClose,
    shouldNavigateOnCancel, // Default to false
}) => {
    const [selectedAccountId, setSelectedAccountId] = React.useState<
        string | null
    >(null);
    const [isModalOpen, setIsModalOpen] = React.useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const [accountsState, accountsAction] = useSfAccountsAction();
    const [hasFetched, setHasFetched] = React.useState(false);

    useEffect(() => {
        const fetchAccounts = async () => {
            if (isUndefined(accountsState.result)) {
                await accountsAction.execute().catch(() => {});
            }
            setHasFetched(true);
        };

        void fetchAccounts();
    }, [accountsAction, accountsState.result]);

    useEffect(() => {
        if (
            hasFetched &&
            !isError(accountsState.status) &&
            isEmpty(accountsState.result)
        ) {
            setIsModalOpen(false);
            onCancel();
        }
    }, [accountsState.result, accountsState.status, hasFetched, onCancel]);

    React.useEffect(() => {
        logWithTakt(TELEMETRY_ID.ACCOUNT_PICKER_OPEN);
    }, []);

    const handleAccountSelect = React.useCallback((accountId: string) => {
        if (!accountId) {
            setIsModalOpen(false);
            onCancel();
            return;
        }
        setSelectedAccountId(accountId);
        logWithTakt(TELEMETRY_ID.ACCOUNT_PICKER_SELECTION, {
            selectedAccountId: accountId,
        });
    }, []);

    const handleUseAccount = React.useCallback(() => {
        if (selectedAccountId) {
            const updatedQueryParams = new URLSearchParams(location.search);
            updatedQueryParams.set("sfId", selectedAccountId);
            navigate(`${location.pathname}?${updatedQueryParams.toString()}`);
            logWithTakt(TELEMETRY_ID.ACCOUNT_SWITCH_SUCCESS, {
                newAccountId: selectedAccountId,
            });
            window.location.reload();
            onClose();
        }
    }, [selectedAccountId, location, navigate, onClose]);

    const handleCancel = React.useCallback(() => {
        setIsModalOpen(false);
        onCancel();
        logWithTakt(TELEMETRY_ID.ACCOUNT_PICKER_CANCEL);
        if (shouldNavigateOnCancel) {
            navigate("/");
        }
    }, [onCancel, shouldNavigateOnCancel, navigate]);

    return hasFetched ? (
        <Modal
            id="account-picker-modal"
            data-testid="account-picker-modal"
            isOpen={isModalOpen}
            header="Please select your Salesforce Account"
            padding="xsmall"
            hasCloseButton={true}
            onClose={handleCancel}
        >
            {isError(accountsState.status) ? (
                <AccountPickerError />
            ) : (
                <>
                    <AccountDropdown
                        selectedAccountId={selectedAccountId}
                        onAccountSelect={handleAccountSelect}
                        onCancel={handleCancel}
                        showNoAccount={true}
                    />
                    <AccountPickerFooter
                        onUseAccount={handleUseAccount}
                        isUseAccountButtonDisabled={isEmpty(selectedAccountId)}
                        isUseAccountButtonPrimary={!!selectedAccountId}
                    />
                </>
            )}
        </Modal>
    ) : null;
};
